import { Autocomplete, TextField, Checkbox, Typography } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { Job, Deal, Contact } from "../../../../api";
import { ImportTableColumn } from "../../../../utilities/types";
import {
	ValidationContext,
	isFieldRequired,
	isFieldVisible,
	isNumericField,
	getValidationErrorKey,
} from "../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../admin/ImportSettings";
import {
	JobsImportTableProps,
	ValidationOptions,
	StringColumnOptions,
	DropdownColumnOptions,
	FileColumnOptions,
	BooleanColumnOptions,
	DateColumnOptions,
	GeoJSONOptionsKey,
	GeoJSONFileKey,
	GroupedGeoJSONOption,
} from "../ImportTable.types";

/**
 * Context object containing all state and helpers that column builders need
 */
export interface ColumnContext {
	// Props
	props: JobsImportTableProps;

	// State
	jobs: Job[];
	setJobs: (jobs: Job[]) => void;
	validationErrors: Record<string, string | undefined>;
	validateJobs: (jobs: Job[], options?: ValidationOptions) => Record<string, string>;
	validationContext: ValidationContext;

	// Salesperson state
	newSalespersons: Contact[];
	setNewSalespersons: (salespersons: Contact[]) => void;
	isNewSalesperson: (id: string) => boolean;
	findNewSalesperson: (id: string) => Contact | undefined;

	// Checkbox group state
	readySelectionState: Record<string, boolean>;
	setReadySelectionState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

	// Dropdown options
	gridSizes: number[];
	depths: number[];
	labPackages: string[];
	micros: number[];

	// Helper functions
	getRowValidationContext: (row: MRT_Row<Job>) => ValidationContext;
	findDeal: (
		deals: Deal[],
		density: number,
		depth: number,
		labPackage: string,
		micros: number | string,
	) => Deal | undefined;
	updateJobGroup: (row: MRT_Row<Job>, jobs: Job[], updateFn: (job: Job) => void) => Job[];
	dealBasedSubRowUpdate: (row: any, field: keyof Job, newValue: any, allDeals: Deal[]) => void;
}

/**
 * Build a text input column
 */
export function textColumn(
	context: ColumnContext,
	fieldName: keyof Job,
	columnId: ImportTableColumn,
	options: StringColumnOptions = {},
): MRT_ColumnDef<Job, unknown> {
	const { hasGroupUpdate = true, inputType = "text", size, minSize, isDisabled } = options;

	return {
		id: columnId,
		header: columnDisplayNames[columnId],
		accessorKey: fieldName,
		enableEditing: true,
		...(size && { size }),
		...(minSize && { minSize }),
		muiEditTextFieldProps: ({ row }) => {
			const errorKey = getValidationErrorKey(columnId, row.original.id);
			const rowContext = context.getRowValidationContext(row);
			const isRequired = isFieldRequired(columnId, rowContext);
			const disabled = isDisabled ? isDisabled(row) : false;

			return {
				type: inputType,
				variant: "outlined",
				required: isRequired,
				disabled: disabled,
				error: !!context.validationErrors?.[errorKey],
				helperText: context.validationErrors?.[errorKey],
				value: context.jobs.find((j) => j.id === row.original.id)?.[fieldName as keyof Job] || "",
				onChange: (event) => {
					if (disabled) return; // Extra safety check

					const value = event.target.value;
					const newJobs = [...context.jobs];
					const newJob = newJobs.find((j) => j.id === row.original.id);
					if (!newJob) return;
					const index = newJobs.indexOf(newJob);
					(newJob as any)[fieldName] = value;

					// Update row cache if specified
					(row._valuesCache as any)[fieldName] = value;

					newJobs[index] = newJob;
					context.setJobs(newJobs);

					context.validateJobs([newJob], { updateState: true, clearPrevious: false });
				},
			};
		},
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
				return (
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						type={inputType}
						onChange={(event) => {
							const newValue = event.target.value;

							const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
								(job as any)[fieldName] = newValue;
							});

							context.setJobs(newJobs);
							context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
						}}
					/>
				);
			},
		}),
	};
}

/**
 * Build a dropdown/autocomplete column
 */
export function selectColumn<T>(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: DropdownColumnOptions<T>,
): MRT_ColumnDef<Job, unknown> {
	const {
		isNumber = false,
		options: dropdownOptions,
		hasGroupUpdate = true,
		size,
		freeSolo = true,
		isRequired: requiredOption,
		getOptionLabel,
		isOptionEqualToValue,
		getValue,
		setValue,
		updateSubRows,
		isDisabled,
	} = options || {};

	const displayName = columnDisplayNames[columnId];
	return {
		id: columnId,
		header: displayName,
		accessorKey: fieldKey as string,
		size: size || (fieldKey === "externalFrequencyOfMicros" ? 100 : 160),
		enableEditing: false,
		Cell: ({ row }: { row: MRT_Row<Job> }) => {
			const job = context.jobs.find((job) => job.id === row.original.id);
			const currentValue = getValue ? getValue(job!) : job?.[fieldKey];
			const displayValue = freeSolo ? ((currentValue?.toString() ?? "") as string) : (currentValue as T);

			// Handle required validation with row-specific context
			const rowContext = context.getRowValidationContext(row);
			let isRequired = false;
			if (typeof requiredOption === "boolean") {
				isRequired = requiredOption;
			} else if (typeof requiredOption === "function") {
				isRequired = requiredOption(context.props.allDeals || []);
			} else {
				isRequired = isFieldRequired(columnId, rowContext);
			}

			// Check if field is disabled
			const disabled = isDisabled ? isDisabled(row) : false;

			const errorKey = getValidationErrorKey(columnId, row.original.id);

			return (
				<Autocomplete
					size="small"
					freeSolo={freeSolo}
					disabled={disabled}
					style={{ width: "100%", padding: 0 }}
					value={displayValue}
					options={dropdownOptions || []}
					getOptionKey={(option) => {
						if (getOptionLabel) return getOptionLabel(option as T);
						return (option as T)?.toString() || "";
					}}
					getOptionLabel={
						getOptionLabel ? (option) => getOptionLabel(option as T) : (option) => (option as T)?.toString() || ""
					}
					isOptionEqualToValue={isOptionEqualToValue}
					renderInput={(params) => (
						<TextField
							style={{ padding: 0 }}
							variant="outlined"
							required={isRequired}
							error={!!context.validationErrors?.[errorKey]}
							helperText={context.validationErrors?.[errorKey]}
							{...params}
						/>
					)}
					onChange={
						freeSolo
							? undefined
							: (event, newValue) => {
									if (disabled) return; // Extra safety check

									const newJobs = [...context.jobs];
									const newJob = newJobs.find((job) => job.id === row.original.id);
									if (!newJob) return;
									const index = newJobs.indexOf(newJob);

									// Use custom setter if provided, otherwise default behavior
									if (setValue) {
										setValue(newJob, newValue as T);
									} else {
										(newJob as any)[fieldKey] = newValue;
									}

									// Update specific cache fields if needed
									if (fieldKey === "branchIds") {
										row._valuesCache.branchIds = newJob.branchIds;
									}

									newJobs[index] = newJob;
									context.setJobs(newJobs);

									context.validateJobs([newJob], { updateState: true, clearPrevious: false });

									// Call updateSubRows if provided
									if (updateSubRows) {
										updateSubRows(row, fieldKey, newValue as T, context.props.allDeals || []);
									}
								}
					}
					onBlur={
						freeSolo
							? (event) => {
									if (disabled) return; // Extra safety check

									const newValue = (event.target as HTMLInputElement).value;
									if (isValidEmptyValue(newValue)) return;

									const newJobs = [...context.jobs];
									const newJob = newJobs.find((job) => job.id === row.original.id);
									if (!newJob) return;
									const index = newJobs.indexOf(newJob);

									// For numeric fields, attempt to convert string to number
									// Store as string if invalid - validateJobs() will handle validation
									if (isNumber) {
										const numValue = Number(newValue);
										(newJob as any)[fieldKey] = isNaN(numValue) ? newValue : numValue;
									} else {
										(newJob as any)[fieldKey] = newValue as unknown as T;
									}

									const finalValue = (newJob as any)[fieldKey];

									// Update cache for specific fields
									if (fieldKey === "externalFrequencyOfMicros") {
										row._valuesCache.externalFrequencyOfMicros = finalValue;
									} else if (fieldKey === "externalTestPackageId") {
										row._valuesCache.labPackage = finalValue;
									} else if (fieldKey === "importDepth") {
										row._valuesCache.importDepth = finalValue;
									} else if (fieldKey === "importDensity") {
										row._valuesCache.importDensity = finalValue;
									}

									// Auto-update dealIds using findDeal for all these fields
									newJob.dealIds = [
										(
											context.findDeal(
												context.props.allDeals,
												newJob.importDensity ?? 0,
												newJob.importDepth ?? 0,
												newJob.externalTestPackageId ?? "",
												newJob.externalFrequencyOfMicros ?? 0,
											)?.id || "custom"
										).toString(),
									];
									row._valuesCache.dealIds = newJob.dealIds;

									newJobs[index] = newJob;
									context.setJobs(newJobs);
									context.validateJobs(fieldKey === "externalFrequencyOfMicros" ? [newJob] : newJobs, {
										updateState: true,
										clearPrevious: false,
									});

									// Call updateSubRows if provided
									if (updateSubRows) {
										updateSubRows(row, fieldKey, finalValue, context.props.allDeals || []);
									}
								}
							: undefined
					}
				/>
			);
		},
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
				return (
					<Autocomplete
						freeSolo={freeSolo}
						style={{ width: "100%" }}
						value={null}
						options={dropdownOptions || []}
						getOptionLabel={
							getOptionLabel ? (option) => getOptionLabel(option as T) : (option) => (option as T)?.toString() || ""
						}
						isOptionEqualToValue={isOptionEqualToValue}
						renderInput={(params) => <TextField variant="outlined" {...params} />}
						onChange={
							freeSolo
								? undefined
								: (event, newValue) => {
										const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
											if (setValue) {
												setValue(job, newValue as T);
											} else {
												(job as any)[fieldKey] = newValue;
											}
										});

										context.setJobs(newJobs);
										context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
									}
						}
						onBlur={
							freeSolo
								? (event) => {
										const newValue = (event.target as HTMLInputElement).value;
										if (newValue === "") return;

										// For numeric fields, attempt to convert string to number
										// Store as string if invalid - validateJobs() will handle validation
										const numValue = isNumber ? Number(newValue) : NaN;
										const valueToStore = isNumber && isNaN(numValue) ? newValue : isNumber ? numValue : newValue;

										const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
											(job as any)[fieldKey] = valueToStore as unknown as T;

											// Auto-update dealIds using findDeal
											job.dealIds = [
												(
													context.findDeal(
														context.props.allDeals,
														job.importDensity ?? 0,
														job.importDepth ?? 0,
														job.externalTestPackageId ?? "",
														job.externalFrequencyOfMicros ?? 0,
													)?.id || "custom"
												).toString(),
											];
										});

										context.setJobs(newJobs);
										context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
									}
								: undefined
						}
					/>
				);
			},
		}),
	};

	function isValidEmptyValue(newValue: string) {
		return (
			!(isNumericField(columnId) && isFieldRequired(columnId, context.validationContext)) &&
			!isFieldRequired(columnId, context.validationContext) &&
			newValue === ""
		);
	}
}

/**
 * Build a checkbox column
 */
export function checkboxColumn(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: BooleanColumnOptions,
): MRT_ColumnDef<Job, unknown> {
	const {
		size = 100,
		minSize = 100,
		hasGroupUpdate = true,
		groupStateKey,
		groupByField = "importGrowerName",
	} = options || {};

	const displayName = columnDisplayNames[columnId];

	return {
		id: columnId,
		header: displayName,
		minSize,
		size,
		accessorKey: fieldKey as string,
		enableEditing: false,
		Cell: ({ row }: { row: MRT_Row<Job> }) => {
			// Ensure we have the latest value from jobs state
			const currentValue = context.jobs.find((f) => f.id === row.original.id)?.[fieldKey] || false;

			return (
				<Checkbox
					checked={currentValue as boolean}
					onChange={(event) => {
						const newJobs = [...context.jobs];
						const newJob = newJobs.find((j) => j.id === row.original.id);
						if (!newJob) return;
						const index = newJobs.indexOf(newJob);
						(newJob as any)[fieldKey] = event.target.checked;
						newJobs[index] = newJob;
						context.setJobs(newJobs);

						// Handle group state synchronization if needed
						if (hasGroupUpdate && groupStateKey && groupByField) {
							const groupValue = String(row.original[groupByField] ?? "");
							const allAreSelected = newJobs
								.filter((j) => String(j[groupByField] ?? "") === groupValue)
								.every((j) => j[fieldKey]);

							// Update group state - this assumes the state exists in scope
							if (groupStateKey === "readySelectionState") {
								context.setReadySelectionState((prevState) => ({
									...prevState,
									[groupValue]: allAreSelected,
								}));
							}
						}

						context.validateJobs([newJob], { updateState: true, clearPrevious: false });
					}}
				/>
			);
		},
		...(hasGroupUpdate &&
			groupByField && {
				AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
					const groupValue = String(row.original[groupByField] ?? "");

					// Get group checkbox state
					let isGroupChecked = false;
					let isIndeterminate = false;

					if (groupStateKey === "readySelectionState") {
						isGroupChecked = context.readySelectionState[groupValue] || false;
						isIndeterminate =
							!isGroupChecked &&
							context.jobs.some((job) => String(job[groupByField] ?? "") === groupValue && job[fieldKey]);
					}

					return (
						<Checkbox
							checked={isGroupChecked}
							indeterminate={isIndeterminate}
							onChange={(event) => {
								const isChecked = event.target.checked;
								const newJobs = context.jobs.map((job) =>
									String(job[groupByField] ?? "") === groupValue ? { ...job, [fieldKey]: isChecked } : job,
								);
								context.setJobs(newJobs);

								// Update group state
								if (groupStateKey === "readySelectionState") {
									context.setReadySelectionState((prevState) => ({
										...prevState,
										[groupValue]: isChecked,
									}));
								}

								context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
							}}
						/>
					);
				},
			}),
	};
}

/**
 * Build a date picker column
 */
export function dateColumn(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: DateColumnOptions,
): MRT_ColumnDef<Job, unknown> {
	const { isRequired: isRequiredOption = false, size, hasGroupUpdate = true } = options || {};

	const displayName = columnDisplayNames[columnId];

	// Helper function to convert Date to YYYY-MM-DD string for input
	const dateToInputString = (date?: Date | null): string => {
		if (!date) return "";
		return date.toISOString().split("T")[0];
	};

	// Helper function to convert YYYY-MM-DD string to Date
	const inputStringToDate = (dateString: string): Date | undefined => {
		if (!dateString) return undefined;
		const date = new Date(dateString + "T00:00:00.000Z");
		return isNaN(date.getTime()) ? undefined : date;
	};

	return {
		id: fieldKey as string,
		header: displayName,
		accessorKey: fieldKey as string,
		size,
		enableEditing: true,
		muiEditTextFieldProps: ({ row }) => {
			const errorKey = getValidationErrorKey(columnId, row.original.id);
			const rowContext = context.getRowValidationContext(row);
			const isRequired =
				typeof isRequiredOption === "boolean" ? isRequiredOption : isFieldRequired(columnId, rowContext);

			return {
				type: "date",
				required: isRequired,
				error: !!context.validationErrors?.[errorKey],
				helperText: context.validationErrors?.[errorKey],
				value: dateToInputString(context.jobs.find((j) => j.id === row.original.id)?.[fieldKey] as Date),
				onChange: (event) => {
					const newJobs = [...context.jobs];
					const newJob = newJobs.find((j) => j.id === row.original.id);
					if (!newJob) return;
					const index = newJobs.indexOf(newJob);
					(newJob as any)[fieldKey] = inputStringToDate(event.target.value);
					newJobs[index] = newJob;
					context.setJobs(newJobs);

					context.validateJobs([newJob], { updateState: true, clearPrevious: false });
				},
			};
		},
		// Display the date in a readable format
		Cell: ({ row }) => {
			const date = context.jobs.find((j) => j.id === row.original.id)?.[fieldKey] as Date;
			return date ? date.toLocaleDateString() : "";
		},
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
				return (
					<TextField
						type="date"
						variant="outlined"
						style={{ width: "100%" }}
						onChange={(event) => {
							const newDate = inputStringToDate(event.target.value);
							const newJobs = [...context.jobs];
							for (const job of newJobs) {
								if (job.importGrowerName === row.original.importGrowerName) {
									(job as any)[fieldKey] = newDate;
								}
							}
							context.setJobs(newJobs);
							context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
						}}
					/>
				);
			},
		}),
	};
}

export function geoJSONFileColumn(
	context: ColumnContext,
	optionsKey: GeoJSONOptionsKey,
	geoJSONKey: GeoJSONFileKey,
	columnId: ImportTableColumn,
	options?: FileColumnOptions,
): MRT_ColumnDef<Job, unknown> {
	const { minSize = 300 } = options || {};

	const displayName = columnDisplayNames[columnId];
	const validationKey = columnId;

	return {
		id: validationKey,
		header: displayName,
		accessorKey: `files.${geoJSONKey}`,
		enableEditing: false,
		minSize,
		Cell: ({ row }: { row: MRT_Row<Job> }) => {
			const jobId = row.original.id;
			const job = context.jobs.find((j) => j.id === jobId) || row.original;

			// Flatten GeoJSONOptionsGroup[] into GroupedGeoJSONOption[]
			const availableOptions: GroupedGeoJSONOption[] = [];
			const optionGroups = job.fileOptions?.[optionsKey] || [];

			optionGroups.forEach((group) => {
				if (group.options && Array.isArray(group.options)) {
					group.options.forEach((opt) => {
						availableOptions.push({
							group: group.type.label,
							option: opt,
						});
					});
				}
			});

			// Get currently selected GeoJSON
			const currentGeoJSON = job.files?.[geoJSONKey];

			// Find the matching option for the current selection
			const selectedOption =
				availableOptions.find((opt) => opt.option.geoJSON === currentGeoJSON) ||
				(currentGeoJSON && availableOptions.length === 1 ? availableOptions[0] : null);

			const errorKey = getValidationErrorKey(validationKey, jobId);
			const hasError = !!context.validationErrors[errorKey];

			// Build file validation context using centralized logic
			const deal = context.props.allDeals?.find((d) => d.id === job.dealIds?.[0]);

			const fileContext: ValidationContext = {
				...context.validationContext,
				job,
				deal,
				isPointsAttached: job.isPointsAttached,
			};

			// Use centralized validation
			const isFieldRequiredValue = isFieldRequired(columnId, fileContext);
			const isFieldHiddenValue = !isFieldVisible(columnId, fileContext);

			return (
				<Autocomplete
					size="small"
					style={{ width: "100%", padding: 0 }}
					options={availableOptions}
					disabled={isFieldHiddenValue}
					renderInput={(params) => (
						<TextField
							style={{ padding: 0 }}
							error={hasError}
							helperText={context.validationErrors[errorKey]}
							required={isFieldRequiredValue}
							{...params}
						/>
					)}
					groupBy={(option) => option.group}
					renderGroup={(params) => (
						<li key={params.key}>
							<Typography variant="caption" sx={{ fontWeight: "bold", pl: 1, color: "text.secondary" }}>
								{params.group}
							</Typography>
							<ul style={{ padding: 0, listStyle: "none" }}>{params.children}</ul>
						</li>
					)}
					value={selectedOption}
					isOptionEqualToValue={(option, value) => {
						if (!option || !value) return false;
						return option.option.name === value.option.name;
					}}
					getOptionLabel={(option) => option?.option?.name || ""}
					onChange={(_, newGroupedOption) => {
						const newGeoJSON = newGroupedOption?.option?.geoJSON || undefined;

						// Update job files directly with GeoJSON - NO CONVERSION NEEDED!
						const newJobs = [...context.jobs];
						const newJob = newJobs.find((j) => j.id === jobId);
						if (!newJob) return;
						const index = newJobs.indexOf(newJob);

						(newJob.files as any)[geoJSONKey] = newGeoJSON;

						newJobs[index] = newJob;
						context.setJobs(newJobs);
						context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
					}}
				/>
			);
		},
	};
}
